<template>
  <div class="play-earworm-container">
    <Header :title="earworm?.name || 'Earworm'" :user="currentUser" :showCloseButton="true" />
    
    <main class="page-main">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Loading earworm...</p>
      </div>
      
      <div v-else-if="error" class="error-state">
        <div class="error-icon">⚠️</div>
        <h3>Error Loading Earworm</h3>
        <p>{{ error }}</p>
      </div>
      
      <div v-else-if="earworm">
        <!-- User Activity Stats -->
        <div v-if="currentUser?.activityStats" class="activity-stats">
          <div class="stat-item">
            <span class="stat-label">Total Plays:</span>
            <span class="stat-value">{{ currentUser.activityStats.totalPlays || 0 }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Test Victories:</span>
            <span class="stat-value">{{ currentUser.activityStats.testVictories || 0 }}</span>
          </div>
        </div>

        <!-- Audio Player Controls -->
        <div class="player-container">
          <div class="audio-controls">
            <div class="audio-info">
              <h2>{{ earworm.name }}</h2>
              <p v-if="isPlaying">Elapsed: {{ formatTime(currentTime) }}</p>
            </div>
            
            <audio 
              ref="audioPlayer"
              :src="currentAudioUrl"
              @loadedmetadata="onAudioLoaded"
              @timeupdate="onTimeUpdate"
              @ended="onAudioEnded"
              @play="isPlaying = true"
              @pause="isPlaying = false"
              preload="metadata"
            />
            
            <!-- Progress Bar -->
            <div v-if="currentDuration" class="progress-container">
              <div class="progress-bar" @click="seekTo">
                <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
                <div class="progress-handle" :style="{ left: progressPercent + '%' }"></div>
              </div>
              <div class="time-display">
                <span>{{ formatTime(currentTime) }}</span>
                <span>{{ formatTime(currentDuration) }}</span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Lyrics Display -->
        <div class="lyrics-container">
          <div v-if="hasTimestampedLyrics && lyricsLines.length" class="simple-lyrics-display">
            <div class="lyrics-content">
              <h3>Lyrics</h3>
              <div class="all-lines-container">
                <div
                  v-for="(line, lineIndex) in lyricsLines"
                  :key="`line-${lineIndex}`"
                  :class="['lyrics-line', { active: lineIndex === currentLineIndex }]"
                >
                  <span
                    v-for="(word, wordIndex) in line.words"
                    :key="`line-${lineIndex}-word-${wordIndex}`"
                    :class="[
                      'word',
                      {
                        active: isWordActive(word),
                        completed: isWordCompleted(word),
                        'spanish-word': word.language === 'spanish'
                      }
                    ]"
                  >
                    {{ word.word }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="no-lyrics">
            <p>No timestamped lyrics available for this track yet.</p>
          </div>
        </div>

        <div class="bottom-controls">
          <div class="track-controls">
            <button
              @click="handlePrimaryPlayback"
              class="track-btn"
              :disabled="!earworm?.audio_url"
            >
              <span v-if="isPlaying" class="pause-icon">⏸</span>
              <span v-else class="play-icon"></span>
              <span class="track-label">{{ isPlaying ? 'Pause' : 'Play' }}</span>
            </button>
            
            <button @click="stopAudio" class="stop-btn" :disabled="!isPlaying">
              <span class="stop-icon"></span>
            </button>
            
            <button @click="openVocabTest" class="test-btn" title="Vocabulary Test">
              Test
            </button>
          </div>
        </div>
      </div>
    </main>

    <!-- Vocabulary Test Modal -->
    <div v-if="showVocabTest" class="vocab-test-modal" @click.self="closeVocabTest">
      <div class="vocab-test-content">
        <!-- Header with avatar and points -->
        <div class="vocab-header">
          <div class="avatar-section">
            <div class="avatar">
              <img v-if="currentUser?.avatar" :src="currentUser.avatar" alt="Avatar" />
              <span v-else>{{ currentUser?.username?.charAt(0)?.toUpperCase() || 'U' }}</span>
            </div>
            <div class="score-display">
              <span class="score">{{ vocabScore }}/15</span>
              <span class="score-label">pts</span>
            </div>
          </div>
          <button @click="closeVocabTest" class="close-btn">✕</button>
        </div>

        <!-- Game Over State -->
        <div v-if="vocabScore <= 0 && vocabTotal > 0" class="game-over">
          <h2>😔 Sorry, try again!</h2>
          <p>You ran out of points. Keep practicing!</p>
          <button @click="() => { recordActivity('earworm-test', earworm.id, { result: 'fail', points: vocabScore, questions: vocabTotal }); resetVocabTest(); }" class="reset-btn">Try Again</button>
        </div>

        <!-- Victory State -->
        <div v-else-if="vocabScore >= 15" class="game-over">
          <h2>🎉 Perfect! Victory!</h2>
          <p>You reached 15 points in {{ vocabTotal }} questions!</p>
          <button @click="() => { recordActivity('earworm-test', earworm.id, { result: 'victory', points: vocabScore, questions: vocabTotal }); resetVocabTest(); }" class="reset-btn">Play Again</button>
        </div>

        <!-- No vocabulary terms available -->
        <div v-else-if="vocabTerms.length === 0" class="game-over">
          <h2>📚 No Vocabulary Available</h2>
          <p>This earworm doesn't have vocabulary terms yet. Try analyzing an image first!</p>
        </div>

        <!-- Active Game -->
        <div v-else-if="currentVocabTerm" class="vocab-game">
          <!-- Falling term -->
          <div class="falling-term" :style="{ top: fallingPosition + '%' }">
            <div class="term-card falling">
              <span class="term-text">{{ currentVocabTerm.term }}</span>
            </div>
          </div>

          <!-- Answer options -->
          <div class="answer-options">
            <button
              v-for="(option, index) in vocabOptions"
              :key="index"
              @click="selectVocabAnswer(option)"
              class="answer-btn"
              :class="{ correct: showResult && option.isCorrect, wrong: showResult && !option.isCorrect && option.selected }"
            >
              {{ option.term }}
            </button>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, onUnmounted, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import Header from '../components/Header.vue'
import { dbService } from '../services/database'

// Interface for earworm data stored in IndexedDB
interface StoredEarworm {
  id: string
  user_id: string | null
  name: string
  source?: string | null
  image_url?: string | null
  analysis?: string | null
  transcript?: string | null
  audio_url?: string | null
  audio_id?: string | null
  status?: string | null
  created_at?: string | Date | null
  modified_at?: string | Date | null
  downloaded_at?: string | Date | null
  audioBlob: Blob | null
}

const router = useRouter()
const route = useRoute()

const currentUser = ref<any>(null)
const loading = ref(true)
const error = ref('')
const earworm = ref<any>(null)
const isPlaying = ref(false)
const currentTime = ref(0)
const currentDuration = ref(0)
const currentLineIndex = ref(-1)
const audioPlayer = ref<HTMLAudioElement | null>(null)
const maxPlaybackTime = ref(0) // Track maximum time reached during playback

// Vocabulary Test State
const showVocabTest = ref(false)
const vocabScore = ref(3) // Start with 3 points
const vocabTotal = ref(0)
const currentVocabTerm = ref<any>(null)
const vocabOptions = ref<any[]>([])
const fallingPosition = ref(0)
const showResult = ref(false)
const vocabTerms = ref<any[]>([])
const fallAnimation = ref<number | null>(null)

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000'

// Audio state
const currentAudioUrl = ref('')

const parseLyricsPayload = (lyricsData: any) => {
  if (!lyricsData) return null;

  if (lyricsData.data && Array.isArray(lyricsData.data.alignedWords)) {
    return { ...lyricsData, alignedWords: lyricsData.data.alignedWords };
  }

  if (typeof lyricsData === "string") {
    try {
      const parsed = JSON.parse(lyricsData);
      if (parsed && parsed.data && Array.isArray(parsed.data.alignedWords)) {
        return { ...parsed, alignedWords: parsed.data.alignedWords };
      }
      return parsed;
    } catch (error) {
      console.error("❌ Failed to parse lyrics string:", error);
      return null;
    }
  }

  if (typeof lyricsData === "object") {
    return lyricsData;
  }

  return null;
};

const currentTimestampedLyrics = computed(() => {
  if (!earworm.value) return null;
  return parseLyricsPayload(earworm.value.transcript);
});

const hasTimestampedLyrics = computed(() => {
  const lyrics = currentTimestampedLyrics.value
  const hasWords = !!lyrics?.alignedWords?.length
  console.log('🎵 Has timestamped lyrics:', hasWords, 'Words count:', lyrics?.alignedWords?.length || 0)
  
  // Debug: Log first few words to verify timestamp structure
  if (hasWords && lyrics.alignedWords.length > 0) {
    console.log('🔍 First 3 words sample:', lyrics.alignedWords.slice(0, 3).map((w: any) => ({
      word: w.word,
      cleaned: cleanLyricsText(w.word),
      start: w.start ?? w.startS,
      end: w.end ?? w.endS
    })))
  }
  
  return hasWords
})

const progressPercent = computed(() => {
  if (!currentDuration.value) return 0
  return (currentTime.value / currentDuration.value) * 100
})

const detectWordLanguage = (word: string): 'spanish' | 'english' | null => {
  if (!word) return null
  const lower = word.toLowerCase()
  if (lower.includes('[spanish')) return 'spanish'
  if (lower.includes('[english')) return 'english'
  return null
}

// Enhanced function to clean lyrics text - removes ALL bracket tags
const cleanLyricsText = (word: string): string => {
  if (!word) return ''
  
  // Remove ALL bracket tags: [spanish], [english], etc.
  return word
    .replace(/\[spanish\]/gi, '')
    .replace(/\[english\]/gi, '')
    .replace(/\[[^\]]+\]/g, '') // Remove any other [tag] patterns
    .replace(/[""]/g, '"') // Normalize quotes
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim()
}

// Process timestamped lyrics into lines with cleaned text
const lyricsLines = computed(() => {
  if (!hasTimestampedLyrics.value) {
    console.log('❌ No timestamped lyrics available')
    return []
  }
  
  const words = currentTimestampedLyrics.value.alignedWords || []
  if (words.length === 0) {
    console.log('❌ No aligned words found')
    return []
  }
  
  console.log('🎵 Processing', words.length, 'words for karaoke display')
  
  // First pass: clean and filter words
  const cleanedWords: any[] = []
  let currentLanguage: 'spanish' | 'english' | null = null
  
  words.forEach((word: any, index: number) => {
    const rawWord = word.word || ''
    const startTime = word.start ?? word.startS ?? word.s
    const endTime = word.end ?? word.endS ?? word.e
    const tagLanguage = detectWordLanguage(rawWord)
    const cleanedText = cleanLyricsText(rawWord)

    // If this token is just a language directive, update context and skip
    if (!cleanedText) {
      if (tagLanguage) {
        currentLanguage = tagLanguage
        return
      }
      console.warn('⚠️ Skipping empty word at index', index, 'Original:', rawWord)
      return
    }

    // Update context if word carried an explicit tag while still having text
    if (tagLanguage) {
      currentLanguage = tagLanguage
    }

    const language = tagLanguage || currentLanguage
    
    cleanedWords.push({
      word: cleanedText,
      start: startTime ?? 0,
      end: endTime ?? (startTime ?? 0) + 0.5,
      originalWord: rawWord, // Keep original for debugging
      language
    })
  })
  
  console.log('✅ Cleaned words:', cleanedWords.length, 'from', words.length, 'original words')
  
  // Second pass: create lines with better phrase detection
  const lines: Array<{ words: any[], startTime: number, endTime: number }> = []
  let currentLine: any[] = []
  let lineStartTime = 0
  
  cleanedWords.forEach((word: any, index: number) => {
    if (currentLine.length === 0) {
      lineStartTime = word.start
    }
    
    currentLine.push(word)
    
    // Improved line breaking logic
    const isLastWord = index === cleanedWords.length - 1
    const wordText = word.word.toLowerCase()
    
    // Detect phrase boundaries
    const endsWithQuote = word.word.endsWith('"')
    const hasIsPattern = wordText.includes(' is ')
    const hasMeansPattern = wordText.includes(' means ')
    const hasLineBreak = word.word.includes('\n')
    
    // Break on natural phrase boundaries
    const endsPhrase = endsWithQuote || hasIsPattern || hasMeansPattern || hasLineBreak
    const isLongLine = currentLine.length >= 10
    
    const shouldBreakLine = isLastWord || endsPhrase || isLongLine
    
    if (shouldBreakLine && currentLine.length > 0) {
      const lineEndTime = word.end || word.start + 1
      lines.push({
        words: [...currentLine],
        startTime: lineStartTime,
        endTime: lineEndTime
      })
      
      if (index < cleanedWords.length - 1) {
        console.log(`📝 Line ${lines.length}: "${currentLine.map(w => w.word).join(' ')}" (${lineStartTime.toFixed(2)}s - ${lineEndTime.toFixed(2)}s)`)
      }
      
      currentLine = []
    }
  })
  
  console.log(`✅ Created ${lines.length} lyric lines from ${cleanedWords.length} cleaned words`)
  if (lines.length > 0) {
    console.log('📋 First line starts at:', lines[0]?.startTime.toFixed(2), 'seconds')
    console.log('📋 Last line ends at:', lines[lines.length - 1]?.endTime.toFixed(2), 'seconds')
  }
  
  return lines
})

// Audio control functions
const playAudio = (restart: boolean = false) => {
  console.log("🎵 Starting playback");

  if (!audioPlayer.value || !currentAudioUrl.value) {
    console.error("❌ Cannot play: audioPlayer or audio URL missing");
    return;
  }

  const duration = currentDuration.value || audioPlayer.value.duration || 0;
  const atBeginning = audioPlayer.value.currentTime <= 0.01;
  const atEnd = duration > 0 && audioPlayer.value.currentTime >= duration;
  const shouldRestart = restart || atBeginning || atEnd;

  if (shouldRestart) {
    audioPlayer.value.pause();
    audioPlayer.value.currentTime = 0;
    currentTime.value = 0;
    currentLineIndex.value = -1;
    audioPlayer.value.load();
  }

  audioPlayer.value
    .play()
    .then(() => console.log("🎵 Playback started"))
    .catch((error) => {
      console.error("Failed to play audio:", error);
    });
};

const handlePrimaryPlayback = () => {
  if (!audioPlayer.value || !currentAudioUrl.value) {
    console.error("❌ Cannot toggle playback: audio source missing");
    return;
  }

  if (isPlaying.value) {
    audioPlayer.value.pause();
    return;
  }

  playAudio();
};

const stopAudio = () => {
  if (!audioPlayer.value) return
  
  audioPlayer.value.pause()
  audioPlayer.value.currentTime = 0
  currentTime.value = 0
  currentLineIndex.value = -1
}

const seekTo = (event: MouseEvent) => {
  if (!audioPlayer.value || !currentDuration.value) return
  
  const progressBar = event.currentTarget as HTMLElement
  const rect = progressBar.getBoundingClientRect()
  const clickX = event.clientX - rect.left
  const percent = clickX / rect.width
  const newTime = percent * currentDuration.value
  
  audioPlayer.value.currentTime = newTime
  currentTime.value = newTime
}

// Audio event handlers
const onAudioLoaded = () => {
  if (audioPlayer.value) {
    currentDuration.value = audioPlayer.value.duration || 0
    console.log('🎵 Audio loaded, duration:', formatTime(currentDuration.value))
  }
}

const onTimeUpdate = () => {
  if (audioPlayer.value) {
    currentTime.value = audioPlayer.value.currentTime
    // Track maximum playback time reached
    if (currentTime.value > maxPlaybackTime.value) {
      maxPlaybackTime.value = currentTime.value
    }
    updateCurrentLine()
    scrollToActiveWord()
  }
}

const onAudioEnded = () => {
  isPlaying.value = false
  currentTime.value = 0
  currentLineIndex.value = -1
  if (audioPlayer.value) {
    audioPlayer.value.currentTime = 0
  }
  
  // Record earworm-listen activity if played more than 50%
  if (earworm.value?.id && currentDuration.value > 0) {
    const playPercentage = (maxPlaybackTime.value / currentDuration.value) * 100
    if (playPercentage > 50) {
		recordActivity('earworm-listen', earworm.value.id, {
			playPercentage,
			listenedSeconds: maxPlaybackTime.value,
			durationSeconds: currentDuration.value
		})
    }
  }
  
  // Reset max playback time for next play
  maxPlaybackTime.value = 0
}

// Lyrics synchronization with improved timing
const updateCurrentLine = () => {
  if (!hasTimestampedLyrics.value || lyricsLines.value.length === 0) {
    return
  }
  
  const currentTimeMs = currentTime.value
  
  // Always start with first line when playing begins
  if (isPlaying.value && currentLineIndex.value === -1 && lyricsLines.value.length > 0) {
    currentLineIndex.value = 0
    console.log('🔧 Setting to first line on play start')
    return
  }
  
  // Find the correct line based on timing with lookahead
  for (let i = 0; i < lyricsLines.value.length; i++) {
    const line = lyricsLines.value[i]
    if (!line) continue
    
    // Use lookahead for smoother display (2 seconds early)
    const lineStart = line.startTime - 2.0
    const nextLineStart = i < lyricsLines.value.length - 1 ? 
      (lyricsLines.value[i + 1]?.startTime ?? line.endTime + 5) : 
      line.endTime + 5 // Show last line for 5 extra seconds
    
    if (currentTimeMs >= lineStart && currentTimeMs < nextLineStart) {
      if (currentLineIndex.value !== i) {
        currentLineIndex.value = i
        console.log(`🎤 Line ${i + 1}/${lyricsLines.value.length}: "${line.words.map(w => w.word).join(' ')}"`)
      }
      return
    }
  }
  
  // If we're before any line starts, show the first line if we're within 10 seconds of it
  if (lyricsLines.value.length > 0 && lyricsLines.value[0] && currentTimeMs < lyricsLines.value[0].startTime) {
    const timeToFirstLine = lyricsLines.value[0].startTime - currentTimeMs
    if (timeToFirstLine <= 10 && currentLineIndex.value !== 0) {
      currentLineIndex.value = 0
      console.log('🔧 Early display of first line, starts in', timeToFirstLine.toFixed(1), 'seconds')
    }
  }
}

const isWordActive = (word: any) => {
  if (!word || typeof word.start !== 'number' || typeof word.end !== 'number') return false
  const time = currentTime.value
  return time >= word.start && time <= word.end
}

const isWordCompleted = (word: any) => {
  if (!word || typeof word.end !== 'number') return false
  return currentTime.value > word.end
}

const scrollToActiveWord = () => {
  const activeWord = document.querySelector('.word.active')
  if (activeWord) {
    activeWord.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest'
    })
  }
}

// Utility functions
const formatTime = (seconds: number) => {
  if (!seconds || isNaN(seconds)) return '0:00'
  
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// IndexedDB functions for offline storage
const openIndexedDB = () => {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open('HopToItEarworms', 1)
    
    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve(request.result)
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result
      
      // Create object store for earworm data
      if (!db.objectStoreNames.contains('earworms')) {
        const store = db.createObjectStore('earworms', { keyPath: 'id' })
        store.createIndex('user_id', 'user_id', { unique: false })
      }
    }
  })
}

const getStoredEarworm = async (earwormId: string): Promise<StoredEarworm | null> => {
  try {
    const db = await openIndexedDB()
    const transaction = db.transaction(['earworms'], 'readonly')
    const store = transaction.objectStore('earworms')
    
    return new Promise<StoredEarworm | null>((resolve) => {
      const request = store.get(earwormId)
      request.onsuccess = () => {
        const stored = request.result
        if (stored) {
          // Convert back to earworm format
          const earworm = {
            id: stored.id,
            user_id: stored.user_id,
            name: stored.name,
            image_url: stored.image_url || undefined,
            analysis: stored.analysis ? JSON.parse(stored.analysis) : undefined,
            transcript: stored.transcript ? JSON.parse(stored.transcript) : undefined,
            audio_url: stored.audio_url || undefined,
            status: stored.status ? JSON.parse(stored.status) : undefined,
            created_at: stored.created_at,
            modified_at: stored.modified_at,
            downloaded_at: stored.downloaded_at,
            audioBlob: stored.audioBlob
          }
          resolve(earworm)
        } else {
          resolve(null)
        }
      }
      request.onerror = () => resolve(null)
    })
  } catch (error) {
    console.error('Failed to get stored earworm:', error)
    return null
  }
}

// Sync queue functions for EarwormPlayer
const addToSyncQueue = async (type: 'activity_record', userId: string, data: any) => {
  try {
    const db = await openIndexedDB()
    const transaction = db.transaction(['syncQueue'], 'readwrite')
    const store = transaction.objectStore('syncQueue')
    
    const syncItem = {
      type,
      user_id: userId,
      data,
      created_at: new Date(),
      retry_count: 0
    }
    
    await store.add(syncItem)
    db.close()
    console.log('📋 Added to sync queue:', type, userId)
  } catch (error) {
    console.error('❌ Failed to add to sync queue:', error)
  }
}

const syncUserData = async () => {
  if (!navigator.onLine) return
  
  try {
    const db = await openIndexedDB()
    const transaction = db.transaction(['syncQueue'], 'readonly')
    const store = transaction.objectStore('syncQueue')
    
    const syncItems = await new Promise<any[]>((resolve) => {
      const request = store.getAll()
      request.onsuccess = () => resolve(request.result || [])
      request.onerror = () => resolve([])
    })
    
    for (const item of syncItems) {
      try {
        if (item.type === 'activity_record') {
          const response = await fetch(`${API_BASE_URL}/api/users/${item.user_id}/activity`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(item.data)
          })
          
          if (response.ok) {
            // Remove from sync queue
            const deleteTransaction = db.transaction(['syncQueue'], 'readwrite')
            const deleteStore = deleteTransaction.objectStore('syncQueue')
            await deleteStore.delete(item.id)
            console.log('✅ Synced activity data for:', item.user_id)
          } else {
            // Increment retry count
            const updateTransaction = db.transaction(['syncQueue'], 'readwrite')
            const updateStore = updateTransaction.objectStore('syncQueue')
            item.retry_count = (item.retry_count || 0) + 1
            await updateStore.put(item)
            
            if (item.retry_count >= 3) {
              await updateStore.delete(item.id)
              console.warn('🗑️ Removed failed sync item after 3 retries:', item.id)
            }
          }
        }
      } catch (error) {
        console.error('❌ Failed to sync item:', item.id, error)
        // Increment retry count
        const updateTransaction = db.transaction(['syncQueue'], 'readwrite')
        const updateStore = updateTransaction.objectStore('syncQueue')
        item.retry_count = (item.retry_count || 0) + 1
        await updateStore.put(item)
        
        if (item.retry_count >= 3) {
          await updateStore.delete(item.id)
          console.warn('🗑️ Removed failed sync item after 3 retries:', item.id)
        }
      }
    }
    
    db.close()
  } catch (error) {
    console.error('❌ Failed to sync user data:', error)
  }
}

// Load earworm data with offline fallback
const loadEarworm = async () => {
  try {
    loading.value = true
    error.value = ''

    const earwormId = route.params.id as string
    console.log('🎵 Loading earworm for playback, ID:', earwormId)

    if (!earwormId || earwormId === 'undefined' || earwormId === 'null') {
      throw new Error('Invalid earworm ID')
    }

    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`
    }

    console.log('📡 Fetching earworm data from API...')
    const response = await fetch(`${API_BASE_URL}/api/earworms/${earwormId}`, { headers })

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Earworm not found')
      }
      throw new Error(`Server error: ${response.status}`)
    }

    const data = await response.json()
    console.log('📡 Received earworm data for player')

    // Log the timestamped lyrics data structure for debugging
    if (data.transcript) {
      const parsed = parseLyricsPayload(data.transcript)
      console.log('🎵 Timestamped lyrics available:', !!parsed?.alignedWords)
    }

    earworm.value = data

    // Check if we have offline audio available
    const storedEarworm = await getStoredEarworm(earwormId)
    if (storedEarworm?.audioBlob) {
      // Use offline audio URL
      const offlineAudioUrl = URL.createObjectURL(storedEarworm.audioBlob)
      currentAudioUrl.value = offlineAudioUrl
      console.log('🎵 Using offline audio for playback')
    } else if (data.audio_url) {
      currentAudioUrl.value = data.audio_url
    }

    if (!currentAudioUrl.value) {
      error.value = 'No audio available for this earworm'
      console.log('❌ No audio URL found in earworm data')
      return
    }

    console.log('✅ Earworm loaded for playback:', data.name)

  } catch (err) {
    console.error('❌ Failed to load earworm from server, trying offline:', err)

    // Try to load from offline storage
    try {
      const earwormId = route.params.id as string
      const storedEarworm = await getStoredEarworm(earwormId)

      if (storedEarworm) {
        console.log('📱 Loading earworm from offline storage')

        // Log the timestamped lyrics data structure for debugging
        if (storedEarworm.transcript) {
          const parsed = parseLyricsPayload(storedEarworm.transcript)
          console.log('🎵 Timestamped lyrics available offline:', !!parsed?.alignedWords)
        }

        earworm.value = storedEarworm

        // Use offline audio if available
        if (storedEarworm.audioBlob) {
          const offlineAudioUrl = URL.createObjectURL(storedEarworm.audioBlob)
          currentAudioUrl.value = offlineAudioUrl
          console.log('🎵 Using offline audio for playback')
        } else {
          error.value = 'Earworm available offline but audio not downloaded'
          return
        }

        console.log('✅ Earworm loaded from offline storage:', storedEarworm.name)
      } else {
        error.value = err instanceof Error ? err.message : 'Failed to load earworm'
      }
    } catch (offlineError) {
      console.error('❌ Failed to load offline earworm:', offlineError)
      error.value = 'Earworm not available online or offline'
    }
  } finally {
    loading.value = false
  }
}

// Watch for audio URL changes to reset player
watch(currentAudioUrl, (newUrl, oldUrl) => {
  console.log('🔄 Audio URL changed')
  currentTime.value = 0
  currentDuration.value = 0
  currentLineIndex.value = -1
  
  if (audioPlayer.value && newUrl) {
    audioPlayer.value.load()
  }
})

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString()
}

// Vocabulary Test Functions
const openVocabTest = () => {
  showVocabTest.value = true
  initializeVocabTerms()
  // Start the test immediately
  startVocabTest()
}

const closeVocabTest = () => {
  showVocabTest.value = false
  resetVocabTest()
}

const resetVocabTest = () => {
  vocabScore.value = 3
  vocabTotal.value = 0
  currentVocabTerm.value = null
  vocabOptions.value = []
  fallingPosition.value = 0
  showResult.value = false
  if (fallAnimation.value) {
    cancelAnimationFrame(fallAnimation.value)
    fallAnimation.value = null
  }
  // Immediately start new game
  nextVocabQuestion()
}

const initializeVocabTerms = () => {
  console.log('🧠 Initializing vocabulary terms...')
  console.log('📊 Earworm analysis data:', earworm.value?.analysis)
  
  if (!earworm.value?.analysis) {
    console.log('❌ No analysis data available')
    return
  }
  
  const analysis = typeof earworm.value.analysis === 'string' 
    ? JSON.parse(earworm.value.analysis) 
    : earworm.value.analysis
  
  console.log('🔍 Parsed analysis:', analysis)
  
  const terms: any[] = []
  
  // Extract vocabulary pairs from analysis
  if (analysis?.vocabulary_pairs) {
    console.log('📚 Found vocabulary_pairs:', analysis.vocabulary_pairs)
    analysis.vocabulary_pairs.forEach((pair: any, index: number) => {
      console.log(`🔤 Pair ${index}:`, pair)
      if (pair.spanish && pair.english) {
        terms.push({
          spanish: pair.spanish.toLowerCase().trim(),
          english: pair.english.toLowerCase().trim()
        })
      } else {
        console.log('⚠️ Invalid pair - missing spanish or english:', pair)
      }
    })
  } else if (analysis?.terms) {
    console.log('📚 Found terms array:', analysis.terms)
    analysis.terms.forEach((pair: any, index: number) => {
      console.log(`🔤 Term ${index}:`, pair)
      if (pair.spanish && pair.english) {
        terms.push({
          spanish: pair.spanish.toLowerCase().trim(),
          english: pair.english.toLowerCase().trim()
        })
      } else {
        console.log('⚠️ Invalid term - missing spanish or english:', pair)
      }
    })
  } else {
    console.log('❌ No vocabulary_pairs or terms found in analysis')
    // Check for alternative vocabulary data structures
    if (analysis?.vocabulary) {
      console.log('🔄 Found alternative vocabulary field:', analysis.vocabulary)
    }
    if (analysis?.words) {
      console.log('🔄 Found words field:', analysis.words)
    }
  }
  
  console.log('✅ Final vocabulary terms:', terms)
  vocabTerms.value = terms
}

const startVocabTest = () => {
  if (vocabTerms.value.length === 0) {
    // Don't show alert, just don't start the game
    return
  }
  
  nextVocabQuestion()
}

const nextVocabQuestion = () => {
  if (vocabTerms.value.length === 0) return
  
  // Reset state
  showResult.value = false
  fallingPosition.value = 0
  if (fallAnimation.value) {
    cancelAnimationFrame(fallAnimation.value)
  }
  
  // Select random term
  const randomIndex = Math.floor(Math.random() * vocabTerms.value.length)
  const selectedPair = vocabTerms.value[randomIndex]
  
  // Randomly choose whether to show Spanish or English
  const showSpanish = Math.random() < 0.5
  const questionTerm = showSpanish ? selectedPair.spanish : selectedPair.english
  const correctAnswer = showSpanish ? selectedPair.english : selectedPair.spanish
  
  currentVocabTerm.value = {
    term: questionTerm,
    language: showSpanish ? 'spanish' : 'english',
    correctAnswer: correctAnswer
  }
  
  // Generate 4 options (1 correct, 1 "match", 2 wrong)
  const options = [correctAnswer, 'match']
  const wrongTerms = vocabTerms.value
    .filter(pair => pair !== selectedPair)
    .map(pair => showSpanish ? pair.english : pair.spanish)
  
  // Add 2 random wrong options
  while (options.length < 4 && wrongTerms.length > 0) {
    const randomWrong = wrongTerms.splice(Math.floor(Math.random() * wrongTerms.length), 1)[0]
    if (!options.includes(randomWrong)) {
      options.push(randomWrong)
    }
  }
  
  // Shuffle options
  vocabOptions.value = options
    .sort(() => Math.random() - 0.5)
    .map(option => ({
      term: option,
      isCorrect: option === correctAnswer,
      selected: false
    }))
  
  // Start falling animation
  startFallingAnimation()
}

const startFallingAnimation = () => {
  const startTime = Date.now()
  const duration = 3000 // 3 seconds
  
  const animate = () => {
    const elapsed = Date.now() - startTime
    const progress = Math.min(elapsed / duration, 1)
    
    fallingPosition.value = progress * 70 // Fall from top to 70% down
    
    if (progress < 1) {
      fallAnimation.value = requestAnimationFrame(animate)
    } else {
      // Time's up - wrong answer
      vocabScore.value = vocabScore.value - 1
      vocabTotal.value++
      showResult.value = true
      
      // Auto-advance after showing result
      setTimeout(() => {
        if (vocabScore.value > 0 && vocabScore.value < 15) {
          nextVocabQuestion()
        }
      }, 1500)
    }
  }
  
  fallAnimation.value = requestAnimationFrame(animate)
}

const selectVocabAnswer = (option: any) => {
  if (showResult.value) return
  
  option.selected = true
  showResult.value = true
  
  vocabTotal.value++
  
  if (option.isCorrect) {
    vocabScore.value++
  } else {
    vocabScore.value = vocabScore.value - 1
  }
  
  // Stop falling animation
  if (fallAnimation.value) {
    cancelAnimationFrame(fallAnimation.value)
    fallAnimation.value = null
  }
  
  // Auto-advance after showing result
  setTimeout(() => {
    if (vocabScore.value > 0 && vocabScore.value < 15) {
      nextVocabQuestion()
    }
  }, 1500)
}

// Activity tracking
const recordActivity = async (
  activityType: 'earworm-listen' | 'earworm-test',
  earwormId: string,
  extra?: any
) => {
  if (!currentUser.value?.id) return

  const details: any = {
    earwormId,
    earwormName: earworm.value?.name || ''
  }

  if (extra && typeof extra === 'object') {
    // Support passing refs directly from template
    const expanded: any = {}
    Object.keys(extra).forEach((k) => {
      const v = (extra as any)[k]
      expanded[k] = v && typeof v === 'object' && 'value' in v ? (v as any).value : v
    })
    Object.assign(details, expanded)
  }

  try {
    await dbService.addUserActivity(currentUser.value.id, activityType, new Date().toISOString(), details)
    console.log(`📋 Activity recorded: ${activityType}`, details)
  } catch (error) {
    console.error('❌ Failed to record activity:', error)
  }
}

const loadUserActivity = async () => {
  if (!currentUser.value?.id) return

  try {
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken

    const response = await fetch(`${API_BASE_URL}/api/users/${currentUser.value.id}`, {
      headers: {
        'Authorization': sessionToken ? `Bearer ${sessionToken}` : ''
      }
    })

    if (response.ok) {
      const userData = await response.json()
      // Update currentUser with activity stats
      if (userData.data?.activity) {
        currentUser.value.activityStats = userData.data.activity
      }
    }
  } catch (error) {
    console.error('Error loading user activity:', error)
  }
}

// Set up online sync
const handleOnline = () => {
  console.log('🌐 Back online - syncing user data')
  syncUserData()
}

onMounted(async () => {
  // Load user data
  try {
    const userStr = sessionStorage.getItem('currentUser')
    if (userStr) {
      currentUser.value = JSON.parse(userStr)
      // Load activity stats
      await loadUserActivity()
    }
  } catch (error) {
    console.error('Error parsing user data:', error)
  }
  
  window.addEventListener('online', handleOnline)
  
  await loadEarworm()
})

onUnmounted(() => {
  // Cleanup audio if playing
  if (audioPlayer.value) {
    audioPlayer.value.pause()
  }
  
  // Remove online event listener
  window.removeEventListener('online', handleOnline)
})
</script>

<style scoped>
.play-earworm-container {
  min-height: 100vh;
  background: #f8f9fa;
}

.page-main {
  padding: 0;
  max-width: 900px;
  margin: 0 auto;
  padding-bottom: 10rem;
}

/* User Activity Stats */
.activity-stats {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 2rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.stat-label {
  font-size: 0.85rem;
  color: #666;
  font-weight: 500;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: bold;
  color: #28a745;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 3rem 1rem;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #43a047;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.player-container {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin-bottom: 2rem;
}

.audio-controls {
  margin-bottom: 1rem;
  text-align: center;
}

.audio-info h2 {
  margin: 0 0 0.5rem 0;
  color: #333;
}

.audio-info p {
  margin: 0 0 1.5rem 0;
  color: #666;
  font-size: 0.95rem;
}

/* Progress bar styles */
.progress-container {
  margin: 1.5rem 0 0.5rem 0;
}

.progress-bar {
  position: relative;
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  cursor: pointer;
  margin-bottom: 0.5rem;
}

.progress-fill {
  height: 100%;
  background: #007bff;
  border-radius: 4px;
  transition: width 0.1s ease;
}

.progress-handle {
  position: absolute;
  top: -4px;
  width: 16px;
  height: 16px;
  background: #007bff;
  border-radius: 50%;
  transform: translateX(-50%);
  transition: left 0.1s ease;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.time-display {
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
  color: #666;
  font-family: monospace;
}

/* Lyrics Container */
.lyrics-container {
  background: white;
  border-radius: 12px;
  margin: 1rem auto 2rem auto;
  max-width: 900px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  padding: 0;
}

.no-lyrics {
  text-align: center;
  color: #666;
  padding: 2rem;
}

.simple-lyrics-display {
  padding: 0;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
}

.lyrics-content h3 {
  margin: 0 0 0.5rem 0;
  color: #333;
  font-size: 1.5rem;
}

.lyrics-subtitle {
  margin: 0 0 1.5rem 0;
  color: #6c757d;
  font-size: 0.95rem;
  font-weight: 500;
}

.all-lines-container {
  max-height: 60vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 5px;
  background: #f8f9fa;
  border-radius: 8px;
}

.lyrics-line {
  margin: 0;
  padding: 0;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  transition: all 0.3s ease;
}

.lyrics-line.active {
  background: rgba(0, 123, 255, 0.1);
  border-left: none;
  padding-left: 0;
  transform: scale(1.02);
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.15);
}

.word {
  display: inline;
  margin-right: 0.35rem;
  color: #333;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  border-radius: 4px;
}

.word.active {
  color: #007bff;
}

.word.spanish-word {
  color: #c62828;
}

.word.active.spanish-word {
  color: #c62828;
}

.word.completed {
  color: #28a745;
  opacity: 0.75;
  font-weight: 500;
}

/* Bottom controls */
.bottom-controls {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 2px solid #e0e0e0;
  padding: 1.25rem 1rem;
  box-shadow: 0 -2px 12px rgba(0,0,0,0.1);
  z-index: 1000;
}

.track-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  max-width: 700px;
  margin: 0 auto;
}

.track-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.25rem;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 100px;
  min-height: 60px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.track-btn:hover:not(:disabled) {
  background: #218838;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

.track-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
  opacity: 0.6;
}

.track-btn.active {
  background: #007bff;
  box-shadow: 0 0 15px rgba(0, 123, 255, 0.5);
}

.stop-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.875rem 1.25rem;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 80px;
  min-height: 60px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.stop-btn:hover:not(:disabled) {
  background: #c82333;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

.stop-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
  opacity: 0.6;
}

.play-icon,
.stop-icon {
  display: inline-block;
}

.play-icon {
  width: 0;
  height: 0;
  border-left: 14px solid white;
  border-top: 9px solid transparent;
  border-bottom: 9px solid transparent;
}

.stop-icon {
  width: 16px;
  height: 16px;
  background: white;
  border-radius: 2px;
}

.track-label {
  font-size: 0.95rem;
  letter-spacing: 0.5px;
}

/* Scrollbar styling */
.all-lines-container::-webkit-scrollbar {
  width: 8px;
}

.all-lines-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.all-lines-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.all-lines-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Responsive design */
@media (max-width: 768px) {
  .page-main {
    padding: 0.5rem;
    padding-bottom: 8rem;
  }
  
  .player-container {
    padding: 1.5rem;
  }
  
  .lyrics-container {
    margin: 0.5rem;
    padding: 0;
  }
  
  .track-controls {
    gap: 0.75rem;
  }
  
  .track-btn,
  .stop-btn {
    min-width: 70px;
    min-height: 55px;
    padding: 0.75rem 1rem;
  }
  
  .track-label {
    font-size: 0.85rem;
  }
  
  .word {
    font-size: 0.85rem;
    margin-right: 0.3rem;
  }
}

@media (max-width: 480px) {
  .lyrics-content h3 {
    font-size: 1.3rem;
  }
  
  .word {
    font-size: 0.8rem;
    margin-right: 0.25rem;
  }
  
  .track-btn,
  .stop-btn {
    min-width: 60px;
    min-height: 50px;
    padding: 0.65rem 0.85rem;
  }
  
  .track-label {
    font-size: 0.8rem;
  }
}

/* Vocabulary Test Modal */
.vocab-test-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.vocab-test-content {
  background: white;
  border-radius: 20px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  padding: 2rem;
  position: relative;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.vocab-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.avatar-section {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #007bff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  font-weight: bold;
}

.avatar img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.score-display {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.score {
  font-size: 1.5rem;
  font-weight: bold;
  color: #28a745;
}

.score-label {
  font-size: 0.8rem;
  color: #666;
  margin-top: -2px;
}

.close-btn {
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.2rem;
  transition: background 0.2s;
}

.close-btn:hover {
  background: #5a6268;
}

.game-over {
  text-align: center;
  padding: 2rem 0;
}

.game-over h2 {
  color: #dc3545;
  margin-bottom: 1rem;
}

.game-over p {
  color: #666;
  margin-bottom: 2rem;
}

.reset-btn {
  background: #28a745;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 10px;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s;
}

.reset-btn:hover {
  background: #218838;
}

.vocab-game {
  position: relative;
  height: 400px;
  overflow: hidden;
  border-radius: 15px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  margin: 0.75rem 0;
}

.falling-term {
  position: absolute;
  left: 20px;
  transform: none;
  transition: none;
  z-index: 10;
}

.term-card {
  background: white;
  padding: 0.5rem 0.75rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  text-align: center;
  min-width: 140px;
  max-width: 160px;
}

.term-card.falling {
  animation: bounce 0.5s ease-in-out;
}

@keyframes bounce {
  0%, 100% { transform: translateX(-50%) scale(1); }
  50% { transform: translateX(-50%) scale(1.05); }
}

.term-text {
  font-size: 0.75rem;
  font-weight: bold;
  color: #333;
  display: block;
  margin-bottom: 0.25rem;
}

.answer-options {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  z-index: 5;
}

.answer-btn {
  background: rgba(255, 255, 255, 0.9);
  border: 2px solid #ddd;
  padding: 0.5rem 0.75rem;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 120px;
  text-align: center;
  white-space: nowrap;
}

.answer-btn:hover {
  background: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.answer-btn.correct {
  background: #28a745;
  color: white;
  border-color: #28a745;
  animation: correctPulse 0.6s ease;
}

.answer-btn.wrong {
  background: #dc3545;
  color: white;
  border-color: #dc3545;
  animation: wrongShake 0.6s ease;
}

@keyframes correctPulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

@keyframes wrongShake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
}

.test-btn {
  background: #17a2b8;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
}

.test-btn:hover {
  background: #138496;
  transform: scale(1.05);
}

.test-icon {
  font-size: 1.2rem;
}
</style>